/**
 * The demo's spaces: one source for the All visits Desk and Parking cells and
 * the details panel's Spaces section, so the two always agree
 * (`concierge-desktop/screens/visitor-spaces.md`, 23 Oct).
 *
 * Priya and Henrik keep their hand-written spaces (`DEMO_VISITOR_DAYS`).
 * Every other visit's `bookings` become a desk on the Birmingham sample floor
 * and a bay in the mock car park (`public/demo/floor-plans`): the design
 * assumes every space has a map (Rahim, 23 Oct). Numbers are handed out in
 * the store's order, never two guests on one space on the same day.
 */

import type { ApiVisit } from '../services/api-types';
import { spaceDay, spaceTimes } from '../spaces/space-times';
import type { ReceptionExtras } from '../visits/visit-row';
import type { SpaceItem } from '../visits/visit-detail-panel/visitor-day';
import { DEMO_LOCATIONS } from './demo-data';
import { DEMO_VISITOR_DAYS } from './demo-visitor-days';

/** The sample floor's desks (1–30) and the mock car park's bays. */
const DESKS = Array.from({ length: 30 }, (_, i) => i + 1);
const BAYS = Array.from({ length: 79 }, (_, i) => i + 1).filter((n) => n % 20 !== 0);

/** A stable start for each visit, so a reload hands out the same spaces. */
function seed(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
}

function dayOf(visit: ApiVisit): string {
  return (visit.expectedDate ?? '').slice(0, 10);
}

function nameNumber(name: string): number | null {
  const n = Number(name.replace(/\D+/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function desk(visit: ApiVisit, n: number, building: string | null): SpaceItem {
  const start = visit.expectedDate ?? '';
  const end = visit.expectedEnd ?? start;
  const times = spaceTimes(start, end);
  return {
    id: `sp-${visit._id}-desk`,
    kind: 'desk',
    name: `Desk ${n}`,
    detail: ['Ground floor', times].filter(Boolean).join(' · '),
    when: [spaceDay(start), times].filter(Boolean).join(' · '),
    times,
    where: 'Ground floor',
    building,
    image: null,
    amenities: [
      { key: 'monitor', label: 'Monitor' },
      { key: 'power', label: 'Power outlets' },
    ],
    status: 'held',
    reason: null,
    map: {
      locationId: 'loc-rrw',
      floorId: 'floor-bham-ground',
      link: { spaceId: `demo-desk-${n}` },
    },
  };
}

function bay(visit: ApiVisit, n: number, building: string | null): SpaceItem {
  const times = $localize`:@@visitors-space-all-day:All day`;
  const reg = visit.visitor?.carRegistration?.trim() || null;
  return {
    id: `sp-${visit._id}-parking`,
    kind: 'parking',
    name: `Bay ${n}`,
    detail: ['Car park B', times, reg].filter(Boolean).join(' · '),
    when: [spaceDay(visit.expectedDate ?? ''), times].filter(Boolean).join(' · '),
    times,
    where: 'Car park B · Level -1',
    building,
    image: null,
    amenities: [],
    status: 'held',
    reason: null,
    map: {
      locationId: 'loc-rrw',
      floorId: 'floor-bham-car-park-b',
      link: { spaceId: `demo-bay-${n}` },
    },
  };
}

/** The next free number from the visit's own start. */
function take(pool: readonly number[], used: Set<number>, from: number): number {
  for (let i = 0; i < pool.length; i++) {
    const n = pool[(from + i) % pool.length];
    if (!used.has(n)) {
      used.add(n);
      return n;
    }
  }
  return pool[from % pool.length];
}

/** Give every demo visit its spaces, in `reception[id].spaces`. */
export function assignDemoSpaces(
  visits: readonly ApiVisit[],
  reception: Record<string, ReceptionExtras>,
): void {
  const used = new Map<string, { desks: Set<number>; bays: Set<number> }>();
  const usedOn = (day: string) => {
    let u = used.get(day);
    if (!u) used.set(day, (u = { desks: new Set(), bays: new Set() }));
    return u;
  };

  // The hand-written ones first: theirs are taken.
  for (const v of visits) {
    const own = DEMO_VISITOR_DAYS[v._id]?.spaces;
    if (!own) continue;
    const u = usedOn(dayOf(v));
    for (const s of own) {
      const n = nameNumber(s.name);
      if (n !== null) (s.kind === 'parking' ? u.bays : u.desks).add(n);
    }
    reception[v._id] = { ...reception[v._id], spaces: own.filter((s) => isRowKind(s.kind)) };
  }

  for (const v of visits) {
    if (DEMO_VISITOR_DAYS[v._id]?.spaces) continue;
    const kinds = reception[v._id]?.bookings ?? [];
    if (!kinds.length) continue;
    const u = usedOn(dayOf(v));
    const building = DEMO_LOCATIONS.find((l) => l._id === v.locationId)?.name ?? null;
    const from = seed(v._id);
    const spaces: SpaceItem[] = [];
    if (kinds.includes('desk')) spaces.push(desk(v, take(DESKS, u.desks, from), building));
    if (kinds.includes('parking')) spaces.push(bay(v, take(BAYS, u.bays, from), building));
    reception[v._id] = { ...reception[v._id], spaces };
  }
}

function isRowKind(kind: SpaceItem['kind']): boolean {
  return kind === 'desk' || kind === 'parking';
}
